import React, { useState, useEffect } from "react";
import AppLayout from "../components/AppLayout";
import { api } from "../api/client";
import { Users, AlertTriangle, CheckCircle2, BookOpen, ChevronDown, ChevronRight, Loader2 } from "lucide-react";
import { toast } from "sonner";

const STATUS_STYLE = {
  completed:   { cls: "bg-lime/10 text-lime",            label: "Completed" },
  in_progress: { cls: "bg-[#7C5CFF]/10 text-[#7C5CFF]", label: "In Progress" },
  not_started: { cls: "bg-white/5 text-gray-500",        label: "Not Started" },
  overdue:     { cls: "bg-red-500/10 text-red-400",       label: "Overdue" },
};

function statusFor(course) {
  if (course.overdue)         return "overdue";
  if (course.progress >= 100) return "completed";
  if (course.progress > 0)    return "in_progress";
  return "not_started";
}

export default function MyTeam() {
  const [data,    setData]    = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter,  setFilter]  = useState("all");
  const [expanded, setExpanded] = useState(new Set());

  useEffect(() => {
    api.get("/my-team")
      .then(setData)
      .catch(() => toast.error("Failed to load team data"))
      .finally(() => setLoading(false));
  }, []);

  const toggleExpand = (id) => setExpanded((prev) => { const next = new Set(prev); next.has(id) ? next.delete(id) : next.add(id); return next; });

  // Flatten all learners across all orgs
  const allLearners = data.flatMap((org) =>
    org.learners.map((l) => ({ ...l, org_name: org.org_name }))
  );

  const totalOverdue   = allLearners.reduce((a, l) => a + l.overdue_count, 0);
  const totalCompleted = allLearners.reduce((a, l) => a + l.courses.filter((c) => c.progress >= 100).length, 0);
  const needsAttention = allLearners.filter((l) => l.overdue_count > 0 || l.avg_progress < 10).length;

  const filtered = allLearners.filter((l) => {
    if (filter === "on_track")  return l.overdue_count === 0 && l.avg_progress > 0;
    if (filter === "overdue")   return l.overdue_count > 0;
    if (filter === "not_started") return l.avg_progress === 0;
    return true;
  });

  return (
    <AppLayout searchPlaceholder="Search team members...">
      {/* Header */}
      <div className="mb-8">
        <h1 className="font-display font-black text-4xl sm:text-5xl tracking-tight">My Team</h1>
        <p className="text-sm text-gray-400 mt-2">
          {data.map((o) => o.org_name).join(" · ") || "No organisations assigned"}
        </p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
        {[
          { label: "Team members",    value: allLearners.length,   icon: Users,         accent: "#CFFF50" },
          { label: "Needs attention", value: needsAttention,       icon: AlertTriangle, accent: "#fbbf24" },
          { label: "Completions",     value: totalCompleted,       icon: CheckCircle2,  accent: "#CFFF50" },
          { label: "Overdue tasks",   value: totalOverdue,         icon: BookOpen,      accent: "#f87171" },
        ].map(({ label, value, icon: Icon, accent }) => (
          <div key={label} className="bg-white/[0.03] border border-white/[0.08] rounded-2xl p-5">
            <Icon size={15} style={{ color: accent }} className="mb-3" />
            <div className="text-3xl font-black">{value}</div>
            <div className="text-xs text-gray-500 mt-1">{label}</div>
          </div>
        ))}
      </div>

      {/* Filter */}
      <div className="flex gap-1 bg-white/[0.03] rounded-full p-1 mb-6 w-fit">
        {[["all","All"],["on_track","On Track"],["not_started","Not Started"],["overdue","Overdue"]].map(([v, l]) => (
          <button key={v} onClick={() => setFilter(v)}
            className={`px-3 py-1.5 rounded-full text-xs font-semibold transition-colors ${filter === v ? "bg-white text-[#0E0F12]" : "text-gray-400 hover:text-white"}`}>
            {l}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-20 text-gray-500">
          <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading team data…
        </div>
      ) : filtered.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-20 border border-white/[0.06] rounded-2xl text-center">
          <Users className="w-10 h-10 text-gray-700 mb-3" />
          <p className="text-gray-400 font-semibold">
            {allLearners.length === 0 ? "No team members yet" : "No members match this filter"}
          </p>
          <p className="text-sm text-gray-600 mt-1">
            {allLearners.length === 0
              ? "Ask your admin to add learners to your organisation."
              : "Try a different filter above."}
          </p>
        </div>
      ) : (
        <div className="border border-white/[0.08] rounded-2xl overflow-hidden">
          {/* Table header */}
          <div className="grid grid-cols-[1fr_140px_140px_140px_40px] gap-0 px-5 py-3 border-b border-white/[0.08] text-xs text-gray-500 uppercase tracking-wider bg-white/[0.02]">
            <span>Member</span>
            <span>Organisation</span>
            <span>Avg Progress</span>
            <span>Overdue</span>
            <span />
          </div>

          {filtered.map((learner) => {
            const isOpen = expanded.has(learner.user_id);
            return (
              <div key={learner.user_id} className="border-b border-white/[0.05] last:border-0">
                {/* Row */}
                <div
                  className="grid grid-cols-[1fr_140px_140px_140px_40px] gap-0 px-5 py-4 hover:bg-white/[0.02] transition-colors cursor-pointer items-center"
                  onClick={() => toggleExpand(learner.user_id)}
                >
                  <div>
                    <p className="font-semibold text-sm">{learner.name}</p>
                    <p className="text-xs text-gray-500">{learner.email}</p>
                  </div>
                  <div className="text-xs text-gray-400">{learner.org_name}</div>
                  <div className="flex items-center gap-2">
                    <div className="w-16 h-1.5 rounded-full bg-white/10 overflow-hidden">
                      <div
                        className={`h-full rounded-full ${learner.avg_progress >= 100 ? "bg-lime" : "bg-[#7C5CFF]"}`}
                        style={{ width: `${learner.avg_progress}%` }}
                      />
                    </div>
                    <span className="text-xs text-gray-400">{learner.avg_progress}%</span>
                  </div>
                  <div>
                    {learner.overdue_count > 0 ? (
                      <span className="inline-flex items-center gap-1 text-xs font-semibold text-red-400">
                        <AlertTriangle size={11} /> {learner.overdue_count} overdue
                      </span>
                    ) : (
                      <span className="text-xs text-gray-600">—</span>
                    )}
                  </div>
                  <div className="flex justify-center">
                    {isOpen ? <ChevronDown size={14} className="text-gray-500" /> : <ChevronRight size={14} className="text-gray-500" />}
                  </div>
                </div>

                {/* Expanded courses */}
                {isOpen && learner.courses.length > 0 && (
                  <div className="px-5 pb-4 bg-white/[0.01]">
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 pt-2">
                      {learner.courses.map((c) => {
                        const s = STATUS_STYLE[statusFor(c)] || STATUS_STYLE.not_started;
                        return (
                          <div key={c.course_id} className="bg-white/[0.03] border border-white/[0.06] rounded-xl p-3">
                            <div className="flex items-start justify-between gap-2 mb-2">
                              <p className="text-xs font-semibold text-gray-300 line-clamp-2">{c.title}</p>
                              <span className={`shrink-0 text-[10px] font-semibold px-2 py-0.5 rounded-full ${s.cls}`}>{s.label}</span>
                            </div>
                            <div className="h-1 rounded-full bg-white/10 overflow-hidden mb-1.5">
                              <div className={`h-full rounded-full ${c.progress >= 100 ? "bg-lime" : c.overdue ? "bg-red-400" : "bg-[#7C5CFF]"}`}
                                style={{ width: `${c.progress}%` }} />
                            </div>
                            <div className="flex items-center justify-between text-[10px] text-gray-600">
                              <span>{c.progress}% complete</span>
                              {c.due_date && <span>Due {c.due_date}</span>}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </AppLayout>
  );
}
